import { useCallback, useEffect, useMemo, useState } from 'react';

import { GroupProps, SimpleGrid } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

import { ButtonMantine } from '../../../../Buttons';
import { Card, CardProps } from '../../../../DataDisplay';
import { Flex, Group, Stack } from '../../../../Layout';
import { Text } from '../../../../Typography';
import { InfoListCardContextProvider, useInfoListCardContext } from './InfoListCard.context';

interface InfoListCardProps extends Omit<CardProps, 'children'>, InfoItemsWrapperProps {
  color?: 'gray' | 'blue';
  header?: React.ReactNode;
  enableUniformLabelWidths?: boolean;
}

type InfoItemsWrapperProps = {
  direction?: 'row' | 'column';
  cols?: 1 | 2;
  children: React.ReactNode | React.ReactNode[];
  labelWidth?: number | string;
};

const InfoListHeader = ({
  title,
  cta,
}: {
  title?: string;
  cta?: { label: string; onClick?: () => void };
}) => {
  return (
    <Group justify="space-between">
      <Text type="sub" fw={600}>
        {title}
      </Text>

      {!!cta && (
        <ButtonMantine variant="subtle" size="xs" onClick={cta?.onClick}>
          {cta?.label}
        </ButtonMantine>
      )}
    </Group>
  );
};

const InfoItem = ({
  label,
  detail,
  labelWidth,
  groupProps,
}: {
  label?: string;
  detail?: string;
  labelWidth?: number | string;
  groupProps?: GroupProps;
}) => {
  const ctx = useInfoListCardContext();
  const { ref, width } = useElementSize();

  // If labelWidth is not provided on the item, use the context value

  const _labelWidth = useMemo(() => {
    let _labelWidth = labelWidth || ctx?.labelWidth;
    if (ctx.enableUniformLabelWidths && ctx.longestLabelWidth > 0) {
      _labelWidth = ctx.longestLabelWidth;
    }
    return _labelWidth || undefined;
  }, [ctx.enableUniformLabelWidths, ctx?.labelWidth, ctx.longestLabelWidth, labelWidth]);

  useEffect(() => {
    if (ctx.enableUniformLabelWidths) {
      ctx.setLongestLabelWidth(width);
    }
  }, [ctx, width]);

  return (
    <Group
      gap={0}
      align="flex-start"
      style={{
        alignContent: 'flex-start',
      }}
      {...groupProps}
    >
      <Text ref={ref} type="caption" fw={500} mr="xs" w={_labelWidth}>
        {label}
      </Text>
      <Text type="caption" c="gray">
        {detail}
      </Text>
    </Group>
  );
};

const InfoItemsWrapper = ({
  direction = 'row',
  cols = 2,
  children,
  labelWidth,
}: InfoItemsWrapperProps) => {
  const { setLabelWidth } = useInfoListCardContext();

  useEffect(() => {
    if (labelWidth) {
      setLabelWidth(labelWidth);
    }
  }, [labelWidth, setLabelWidth]);

  if (direction === 'row') {
    return (
      <Flex rowGap={4} columnGap="md" wrap="wrap" mah="100%">
        {children}
      </Flex>
    );
  }

  return (
    <SimpleGrid verticalSpacing={4} cols={cols}>
      {children}
    </SimpleGrid>
  );
};

const InfoListCard = ({
  color = 'gray',
  header,
  direction,
  cols,
  labelWidth,
  children,
  enableUniformLabelWidths = false,
  ...props
}: InfoListCardProps) => {
  const cardColor = color === 'gray' ? 'gray.0' : 'cobalt.0';
  const [_labelWidth, setLabelWidth] = useState<number | string | undefined>(labelWidth);
  const [longestLabelWidth, _setLongestLabelWidth] = useState(0);

  const setLongestLabelWidth = useCallback((width: number) => {
    _setLongestLabelWidth((prev) => Math.max(prev, width));
  }, []);

  if (!children) return null;

  return (
    <InfoListCardContextProvider
      value={{
        setLabelWidth,
        labelWidth: _labelWidth,
        enableUniformLabelWidths,
        longestLabelWidth,
        setLongestLabelWidth,
      }}
    >
      <Card bg={cardColor} radius="sm" {...props}>
        <Stack>
          {header}
          <InfoItemsWrapper direction={direction} cols={cols}>
            {children}
          </InfoItemsWrapper>
        </Stack>
      </Card>
    </InfoListCardContextProvider>
  );
};

InfoListCard.Header = InfoListHeader;
InfoListCard.Item = InfoItem;

export { InfoListCard };
